import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/utils/api';
import Header from '@/components/Header';
import ServiceMeta from '@/components/ServiceMeta';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Sparkles, Upload, Loader2, ArrowLeft, CheckCircle } from 'lucide-react';

const VevoServicePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    artist_name: '',
    video_title: ''
  });
  const [videoFile, setVideoFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!videoFile) {
      toast.error('Veuillez uploader votre clip vidéo');
      return;
    }
    
    setSubmitting(true);
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('artist_name', formData.artist_name);
      formDataToSend.append('video_title', formData.video_title);
      formDataToSend.append('video', videoFile);
      
      await api.post('/service/vevo/submit', formDataToSend, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setSubmitted(true);
      toast.success('✅ Clip soumis ! Procédez au paiement');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de soumission');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen pb-12">
        <ServiceMeta 
          title="Chaîne VEVO Officielle - Distribution Clips Musicaux"
          description="Publiez vos clips musicaux sur VEVO, la plateforme officielle des artistes professionnels, pour seulement 30$ via AGRP BOX."
          url="/creer-vevo"
        />
        <Header />
        
        <main className="pt-24 max-w-3xl mx-auto px-4">
          <Card className="glass border-zinc-800 text-center">
            <CardContent className="py-12">
              <CheckCircle className="mx-auto mb-4 text-green-500" size={64} />
              <h2 className="text-2xl font-bold text-white mb-3">
                Clip Reçu ! 🎉
              </h2>
              <p className="text-zinc-400 mb-6">
                Procédez au paiement pour la mise en ligne sur VEVO officiel.
              </p>
              <div className="bg-zinc-900 rounded-lg p-6 mb-6">
                <p className="text-3xl font-bold text-[#F97316] mb-3">30$</p>
                <Button className="w-full bg-gradient-to-r from-pink-600 to-pink-800 hover:opacity-90 py-6 text-lg">
                  PAYER 30$ MAINTENANT
                </Button>
              </div>
              <Button onClick={() => navigate('/dashboard')} variant="outline">
                Retour au Dashboard
              </Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-12">
      <ServiceMeta 
        title="Chaîne VEVO Officielle - Distribution Clips Musicaux"
        description="Publiez vos clips musicaux sur VEVO, la plateforme officielle des artistes professionnels, pour seulement 30$ via DYVY'S Afrique."
        url="/creer-vevo"
      />
      <Header />
      
      <main className="pt-24 max-w-3xl mx-auto px-4">
        <Button 
          onClick={() => navigate('/dashboard')}
          variant="ghost"
          className="mb-6"
        >
          <ArrowLeft className="mr-2" size={18} />
          Retour
        </Button>

        <Card className="glass border-zinc-800">
          <CardHeader>
            <CardTitle className="flex items-center gap-3 text-2xl">
              <Sparkles className="text-pink-500" size={32} />
              Configurer Ma Chaîne VEVO - 30$
            </CardTitle>
            <p className="text-zinc-400 text-sm mt-2">
              Étape 1 : Uploadez votre clip. Paiement ensuite.
            </p>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label>Nom de l'Artiste *</Label>
                <Input
                  value={formData.artist_name}
                  onChange={(e) => setFormData({...formData, artist_name: e.target.value})}
                  required
                  placeholder="Ex: Raoul MLELO"
                  className="bg-zinc-900 border-zinc-800 text-white"
                />
              </div>

              <div>
                <Label>Titre du Clip *</Label>
                <Input
                  value={formData.video_title}
                  onChange={(e) => setFormData({...formData, video_title: e.target.value})}
                  required
                  placeholder="Ex: Mon Nouveau Clip"
                  className="bg-zinc-900 border-zinc-800 text-white"
                />
              </div>

              <div>
                <Label>Fichier Vidéo (MP4, MOV) *</Label>
                <div className="mt-2">
                  <label className="flex items-center justify-center w-full p-6 border-2 border-dashed border-zinc-700 rounded-lg cursor-pointer hover:border-pink-500 transition-colors bg-zinc-900">
                    <div className="text-center">
                      <Upload className="mx-auto mb-2 text-zinc-500" size={32} />
                      <p className="text-sm text-zinc-400">
                        {videoFile ? videoFile.name : 'Cliquez pour uploader votre clip'}
                      </p>
                      <p className="text-xs text-zinc-600 mt-1">HD ou 4K recommandé</p>
                    </div>
                    <input
                      type="file"
                      accept="video/*"
                      onChange={(e) => setVideoFile(e.target.files[0])}
                      className="hidden"
                      required
                    />
                  </label>
                </div>
              </div>

              <div className="bg-pink-900/20 border border-pink-800 rounded-lg p-4 text-sm text-pink-200">
                <strong>✨ Ce que vous obtenez :</strong>
                <ul className="mt-2 space-y-1 ml-4 list-disc">
                  <li>Mise en ligne sur VEVO officiel</li>
                  <li>Visibilité mondiale</li>
                  <li>Crédibilité professionnelle</li>
                  <li>Partage automatique sur YouTube</li>
                </ul>
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-gradient-to-r from-pink-600 to-pink-800 hover:opacity-90 py-6 text-lg font-semibold"
              >
                {submitting ? <Loader2 className="animate-spin" /> : 'CONTINUER VERS LE PAIEMENT'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default VevoServicePage;
